<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { trackEvent } from '@/composables/useAnalytics'
import AppLayout from '@/Layouts/AppLayout.vue'
import { CheckCircle, Loader2, AlertCircle, PartyPopper, Calendar, Video, Users } from 'lucide-vue-next'

interface Subscription {
  id: number
  status: string
  teacher_name: string
  teacher_slug: string
  tier_name: string
  tier_slug: string
  amount: number
  has_live_sessions: boolean
  selected_slot?: {
    day?: string
    time?: string
    subject_name?: string
    level_name?: string
  }
}

interface Props {
  subscriptionId: string
  validationWindowHours?: number
  confirmationDeadline?: string | null
  firstLesson?: {
    at_risk: boolean
    this_week: string
    guaranteed: string
  } | null
  subscription: Subscription | null
}

const props = defineProps<Props>()

// State
const verificationStatus = ref<'verifying' | 'complete' | 'processing' | 'error'>('verifying')
const verifiedSubscription = ref<Subscription | null>(props.subscription)
const errorMessage = ref('')

// Non-blocking scheduling state
const isSettingUpSessions = ref(false)
const sessionsSetupComplete = ref(false)

// Check if this is a live session tier (silver or gold)
const hasLiveSessions = computed(() => {
  const sub = verifiedSubscription.value
  return sub?.has_live_sessions || sub?.tier_slug === 'silver' || sub?.tier_slug === 'gold'
})

/**
 * This page no longer authenticates anything.
 *
 * 3-D Secure is settled on the payment page, where the card form still is, and
 * the subscription exists before we ever get here — so there is nothing to wait
 * for and no fixed five second spinner. Reaching this page means the money is
 * held and the tutor has been asked.
 */
onMounted(async () => {
  // Already accepted by the tutor.
  if (props.subscription && props.subscription.status === 'active') {
    handleConfirmedPayment(props.subscription)

    return
  }

  await verifySubscription()
})

async function verifySubscription() {
  try {
    const response = await fetch('/student/subscriptions/verify-status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        subscription_id: props.subscriptionId,
      }),
    })

    const data = await response.json()

    if (data.status === 'confirmed') {
      verifiedSubscription.value = data.subscription
      handleConfirmedPayment(data.subscription)
    } else if (data.status === 'awaiting_tutor') {
      verifiedSubscription.value = data.subscription
      verificationStatus.value = 'awaiting_tutor'
      // Do not poll because we are just waiting for the tutor to check their email
    } else if (data.status === 'pending') {
      if (data.subscription) {
        verifiedSubscription.value = data.subscription
      }
      verificationStatus.value = 'processing'
    } else if (data.status === 'error') {
      verificationStatus.value = 'error'
      errorMessage.value = data.message || 'An error occurred while verifying your payment.'
    } else {
      verificationStatus.value = 'processing'
    }
  } catch (error) {
    console.error('Verification failed:', error)
    verificationStatus.value = 'error'
    errorMessage.value = 'Unable to verify payment status. Please check My lessons.'
  }
}

function handleConfirmedPayment(subscription: Subscription) {
  verifiedSubscription.value = subscription
  verificationStatus.value = 'complete'

  trackEvent('purchase', {
    transaction_id: props.subscriptionId,
    tutor_name: subscription.teacher_name,
    tier: subscription.tier_name,
    value: subscription.amount,
    currency: 'GBP',
  })

  // For live session tiers, show non-blocking scheduling indicator
  if (subscription.has_live_sessions || subscription.tier_slug === 'silver' || subscription.tier_slug === 'gold') {
    startSessionSetup()
  }
}

async function startSessionSetup() {
  isSettingUpSessions.value = true

  // Simulate session setup (actual work happens on backend via webhook/verify)
  await new Promise(resolve => setTimeout(resolve, 2500))

  isSettingUpSessions.value = false
  sessionsSetupComplete.value = true
}

function goToDashboard() {
  router.visit('/student/dashboard')
}

function goToSubscriptions() {
  router.visit('/student/subscriptions')
}

function goToTutorProfile() {
  if (verifiedSubscription.value?.teacher_slug) {
    router.visit(`/tutors/${verifiedSubscription.value.teacher_slug}`)
  }
}

</script>

<template>
  <AppLayout>
    <main class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <div class="max-w-md w-full">
        <!-- Verifying State -->
        <div v-if="verificationStatus === 'verifying'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 class="w-10 h-10 text-blue-600 animate-spin" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Confirming Your Booking</h1>
          <p class="text-gray-600 mb-4">
            Your payment is held and we are just checking the details...
          </p>
        </div>

        <!-- Complete State -->
        <div v-else-if="verificationStatus === 'complete'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
            <CheckCircle class="w-12 h-12 text-green-600" />
            <PartyPopper class="w-6 h-6 text-yellow-500 absolute -top-1 -right-1" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">You're All Set!</h1>
          <p class="text-gray-600 mb-6">
            Your subscription is now active.
          </p>

          <!-- Subscription Details -->
          <div v-if="verifiedSubscription" class="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Tutor</span>
                <span class="font-medium text-gray-900">{{ verifiedSubscription.teacher_name }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Plan</span>
                <span
                  :class="[
                    'font-medium px-2 py-0.5 rounded-full text-xs',
                    verifiedSubscription.tier_slug === 'gold'
                      ? 'bg-yellow-100 text-yellow-800'
                      : verifiedSubscription.tier_slug === 'silver'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ verifiedSubscription.tier_name }}
                </span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Amount</span>
                <span class="font-medium text-gray-900">&pound;{{ verifiedSubscription.amount.toFixed(2) }}/lesson</span>
              </div>

              <!-- Session info for live tiers -->
              <template v-if="hasLiveSessions && verifiedSubscription.selected_slot">
                <hr class="border-gray-200" />
                <div class="flex items-start gap-2">
                  <component
                    :is="verifiedSubscription.tier_slug === 'gold' ? Video : Users"
                    class="w-4 h-4 text-gray-500 mt-0.5"
                  />
                  <div class="text-sm">
                    <div class="text-gray-600">Your session time</div>
                    <div class="font-medium text-gray-900">
                      {{ verifiedSubscription.selected_slot.day }} at {{ verifiedSubscription.selected_slot.time }}
                    </div>
                    <div v-if="verifiedSubscription.selected_slot.subject_name" class="text-gray-500 text-xs">
                      {{ verifiedSubscription.selected_slot.subject_name }}
                      <span v-if="verifiedSubscription.selected_slot.level_name">
                        - {{ verifiedSubscription.selected_slot.level_name }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- Non-blocking session setup indicator -->
          <div v-if="hasLiveSessions" class="mb-4">
            <div
              v-if="isSettingUpSessions"
              class="flex items-center justify-center gap-2 text-sm text-purple-600 bg-purple-50 rounded-lg py-2 px-3"
            >
              <Loader2 class="w-4 h-4 animate-spin" />
              <span>Setting up your {{ verifiedSubscription?.tier_slug === 'gold' ? '1:1' : 'group' }} sessions...</span>
            </div>
            <div
              v-else-if="sessionsSetupComplete"
              class="flex items-center justify-center gap-2 text-sm text-green-600 bg-green-50 rounded-lg py-2 px-3"
            >
              <CheckCircle class="w-4 h-4" />
              <span>Sessions scheduled!</span>
            </div>
          </div>

          <div class="space-y-3">
            <button
              @click="goToDashboard"
              class="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              Go to Dashboard
            </button>
            <button
              @click="goToSubscriptions"
              class="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              View My Subscriptions
            </button>
          </div>

          <p class="text-sm text-gray-500 mt-4">
            A confirmation email has been sent to your inbox.
          </p>
        </div>

        <!-- Awaiting Tutor State (Subscription pending approval) -->
        <div v-else-if="verificationStatus === 'awaiting_tutor'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock class="w-12 h-12 text-blue-600" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h1>
          <p class="text-gray-600 mb-4">
            Your card has been authorised but not charged. We've sent your request to the tutor, who has
            <template v-if="confirmationDeadline">until <strong>{{ confirmationDeadline }} UK time</strong></template>
            <template v-else>{{ validationWindowHours }} hours</template>
            to approve it.
          </p>

          <!-- When the first lesson will actually be -->
          <div
            v-if="firstLesson"
            class="rounded-xl px-4 py-3 mb-6 text-sm text-left bg-emerald-50 border border-emerald-200 text-emerald-900"
          >
            Once your tutor confirms, your first lesson will be <strong>{{ firstLesson.guaranteed }}</strong>.
            <template v-if="firstLesson.at_risk">
              {{ firstLesson.this_week }} is less than two days away, so lessons start the week after.
            </template>
          </div>

          <!-- Show subscription details -->
          <div v-if="verifiedSubscription" class="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Tutor</span>
                <span class="font-medium text-gray-900">{{ verifiedSubscription.teacher_name }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Plan</span>
                <span class="font-medium text-gray-900">{{ verifiedSubscription.tier_name }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Amount</span>
                <span class="font-medium text-gray-900">&pound;{{ verifiedSubscription.amount.toFixed(2) }}/lesson</span>
              </div>
            </div>
          </div>

          <div class="space-y-3">
            <button
              @click="goToDashboard"
              class="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              Go to Dashboard
            </button>
            <button
              @click="goToSubscriptions"
              class="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              View My Subscriptions
            </button>
          </div>
          <p class="text-sm text-gray-500 mt-4">
            You'll receive an email as soon as the tutor accepts your request.
          </p>
        </div>

        <!-- Pending State (subscription created, webhook hasn't confirmed yet) -->
        <div v-else-if="verificationStatus === 'processing'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 class="w-10 h-10 text-yellow-600 animate-spin" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Subscription Pending</h1>
          <p class="text-gray-600 mb-6">
            Your subscription has been created and is awaiting payment confirmation.
          </p>

          <!-- Show subscription details -->
          <div v-if="verifiedSubscription" class="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Tutor</span>
                <span class="font-medium text-gray-900">{{ verifiedSubscription.teacher_name }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Plan</span>
                <span class="font-medium text-gray-900">{{ verifiedSubscription.tier_name }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Amount</span>
                <span class="font-medium text-gray-900">&pound;{{ verifiedSubscription.amount.toFixed(2) }}/lesson</span>
              </div>
            </div>
          </div>

          <div class="space-y-3">
            <button
              @click="goToDashboard"
              class="w-full py-3 rounded-xl font-semibold transition-all bg-gradient-to-r from-teal-500 to-teal-800 text-white hover:from-blue-700 hover:to-purple-700"
            >
              Go to Dashboard
            </button>
            <button
              @click="goToSubscriptions"
              class="w-full py-3 rounded-xl font-semibold transition-all bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              View My Subscriptions
            </button>
          </div>
          <p class="text-sm text-gray-500 mt-4">
            You'll receive an email confirmation once your subscription is active.
          </p>
        </div>

        <!-- Error State -->
        <div v-else-if="verificationStatus === 'error'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle class="w-12 h-12 text-red-600" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
          <p class="text-gray-600 mb-6">
            {{ errorMessage }}
          </p>
          <div class="space-y-3">
            <button
              @click="goToSubscriptions"
              class="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
            >
              Check Status
            </button>
            <button
              @click="goToDashboard"
              class="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Go to Dashboard
            </button>
          </div>
          <p class="text-sm text-gray-500 mt-4">
            If you continue to have issues, please contact support.
          </p>
        </div>
      </div>
    </main>
  </AppLayout>
</template>
